<script lang="ts">
  import { Control, setupControl } from "./controls";
  import FormField from "./FormField.svelte";
  import { Validator } from "./validators";
  import type { ControlValidator, ValidatorMessages } from "./validators";
  import { createEventDispatcher } from "svelte";

  const dispatch = createEventDispatcher<{ change: Control }>();

  // either set name and validators
  export let name: string = null;
  export let disabled: boolean = false;
  export let required: boolean = false;
  export let validators: ControlValidator[] = [];
  export let value: any = true;
  export let checked: boolean = false;
  // ... or bind a control
  export let control: Control = null;
  export let standalone: boolean = false;
  export let displayName: string = undefined;
  export let errors: ValidatorMessages = undefined;

  const changes = setupControl(Control, {
    name,
    validators: [...validators, ...(required ? [Validator.required] : [])],
    value: getValue(checked),
    control,
    standalone,
  });
  $: ctrl = $changes;

  let oldChecked = checked;
  $: if (checked !== oldChecked) {
    oldChecked = checked;
    ctrl.value = getValue(checked);
  }

  let oldValue = value;
  $: if (value !== oldValue) {
    oldValue = value;
    ctrl.value = getValue(checked);
  }

  function getValue(checked: boolean) {
    return checked ? value : value === true ? false : undefined;
  }

  function handleInput(e: Event) {
    ctrl.value = getValue((e.target as HTMLInputElement).checked);
    dispatch("change", ctrl);
  }
</script>

<FormField bind:control={ctrl} {displayName} {errors} {standalone}>
  <label class="scom-checkbox scom-checkbox--large bs3-align-items-baseline">
    <input type="checkbox" {name} {value} checked={ctrl.value === value} on:input={handleInput} {disabled} />
    <i />
    &nbsp;<span><slot /></span>
  </label>
</FormField>

<style lang="scss" global>
  :where(.scom-checkbox) {
    display: inline-flex;
    align-items: center;
    cursor: pointer;

    input {
      // soft hide - because jQuery form serialize breaks if we realy hide it!
      position: absolute;
      height: 1px;
      width: 1px;
      margin: -1px;
      padding: 0;
      border: 0;
      clip: rect(0 0 0 0);
      overflow: hidden;
      opacity: 0;
    }

    i {
      flex: 0 0 auto;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      height: 1em;
      width: 1em;
      margin-right: 0.4em;
      // visuals:
      background-color: #fff;
      border-radius: 0.175rem;
      transition: border ease-in-out 100ms, background ease-in-out 300ms;
      border: 1px solid #a6a6a6;

      &:after {
        content: "";
        position: relative;
        top: -12%;
        left: 3%;
        width: 0.4em;
        height: 0.8em;
        background-color: transparent;
        border: solid #ffbc00;
        border-width: 0 2px 2px 0;
        // border-width: 0 0.08em 0.08em 0;
        opacity: 0;
        transition: transform ease-out 500ms, opacity ease-in-out 100ms;
        transform: rotate(55deg);
      }
    }

    input:checked ~ i {
      background-color: #000;
      border-color: #000;

      &:after {
        opacity: 1;
        transform: rotate(45deg);
      }
    }

    input:disabled ~ * {
      opacity: 0.4;
    }

    input:focus ~ i {
      border-color: #000;
      box-shadow: 0 0 0 1px #000;
    }
  }

  :where(.scom-checkbox--large) i {
    font-size: 1.34rem;
    position: relative;
    bottom: -0.2rem;

    &:after {
      border-width: 0 0.1em 0.1em 0;
    }
  }

  :where(.scom-checkbox--xlarge) i {
    font-size: 2rem;
    position: relative;
    bottom: -0.2rem;

    &:after {
      border-width: 0 0.1em 0.1em 0;
    }
  }
</style>
